import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { colorsMapping } from '@repo/toolkit/colors'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import DetailedTable, { DetailedTableColumn } from '@repo/ui/detailed-table'
import { Skeleton } from '@repo/ui/skeleton'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { Fragment } from 'react'

import { getTripFolderMissions } from '../../../api/trip-folder/get-trip-folder-missions'
import {
  MissionTypeEnum,
  missionTypeEnumMapping
} from '../../../lib/types/enums/mission/mission-type.enum'
import { Mission } from '../../../lib/types/models/mission/mission'
import { formatPhoneNumber } from '../../../utils/functions/format-phone-number'
import UnassignTripFolderMissionButton from './unassign-trip-folder-mission-button'

const officeBorderColors = [
  tailwindColors.grey.light.DEFAULT,
  tailwindColors.secondary.light.hover,
  tailwindColors.blue.light.active
]

interface Props {
  folderId: number
}

interface TripFolderMission {
  appointmentTime: string
  missionLabel: string
  missionType: MissionTypeEnum
  staffs: string[]
  description?: string
  id: number
}

function getDisplayName(firstName: string, lastName: string, phoneNumber?: string): string {
  return `${toFullName(firstName, lastName, { options: { mode: 'lastname-firstname' } })} (${valueRender(phoneNumber, formatPhoneNumber)})`
}

const formatMissions = (missions: Mission[]): TripFolderMission[] => {
  return missions.map((mission) => {
    const staffs = mission.affectedStaffs.map(({ staff }) =>
      getDisplayName(staff.firstName, staff.lastName, staff.phoneNumber)
    )
    const externalStaffs = mission.affectedExternalStaffs.map((s) =>
      getDisplayName(s.firstName, s.lastName, s.phone)
    )
    const lecStaffs = mission.affectedLecStaffs.map(({ user }) =>
      getDisplayName(user.firstName, user.lastName, user.phoneNumber)
    )

    return {
      appointmentTime: DateFormatter.getFormattedTime(mission.appointmentTime),
      missionLabel: mission.title,
      missionType: mission.type,
      staffs: [...staffs, ...externalStaffs, ...lecStaffs],
      description: mission.description,
      id: mission.id
    }
  })
}

const TripFolderMissionSection = ({ folderId }: Props) => {
  const { data: missions, isPending } = useQuery({
    queryKey: ['trip-folder-missions', folderId],
    queryFn: () => getTripFolderMissions(folderId),
    select: formatMissions
  })

  const columns: DetailedTableColumn<TripFolderMission>[] = [
    {
      header: 'Heure de RDV',
      key: 'appointmentTime',
      render: (row) => (
        <Badge text={valueRender(row.appointmentTime, (value) => value || '-')} variant="grey" />
      )
    },
    {
      header: 'Missions',
      key: 'missionLabel',
      render: (row) => (
        <div className="flex items-center gap-1">
          <Badge
            text={
              <div className="flex items-center gap-1">
                <Typography variant="label-medium">{row.missionLabel} - </Typography>
                <Typography
                  variant="label-medium"
                  color={
                    colorsMapping[missionTypeEnumMapping[row.missionType].variant ?? 'grey'].text
                  }
                >
                  {missionTypeEnumMapping[row.missionType].label}
                </Typography>
              </div>
            }
            className="first:mt-2 mb-2 h-fit !py-3 w-max"
            color={tailwindColors.white.hover}
            borderColor={tailwindColors.grey.light.DEFAULT}
          />

          <UnassignTripFolderMissionButton folderId={folderId} missionId={row.id} />
        </div>
      )
    },
    {
      header: 'Bureau LEC/AAT',
      key: 'staffs',
      render: (row) => {
        const borderColor = officeBorderColors.shift() || officeBorderColors[0]
        officeBorderColors.push(borderColor) // Move the first color to the end

        return (
          <Fragment>
            {row.staffs.length === 0 ? (
              <Badge
                text="-"
                color={tailwindColors.white.hover}
                borderColor={borderColor}
                className="first:mt-2 mb-2 h-fit py-1.5 w-max"
              />
            ) : (
              row.staffs.map((staff) => (
                <Badge
                  key={staff}
                  text={staff}
                  color={tailwindColors.white.hover}
                  borderColor={borderColor}
                  className="first:mt-2 mb-2 h-fit py-1.5 w-max"
                />
              ))
            )}
          </Fragment>
        )
      }
    },
    {
      header: 'Description',
      key: 'description',
      render: ({ description }) => (
        <Typography variant="secondary-body" className="py-2 min-w-56">
          {description ?? '-'}
        </Typography>
      )
    }
  ]

  if (isPending) {
    return <Skeleton className="h-[300px] w-full" />
  }

  if (!isPending && (!missions || missions?.length === 0)) {
    return (
      <div className="flex justify-center items-center p-8 bg-gray-50 rounded-3xl border border-primary-light">
        <Typography variant="secondary-body">Aucune mission disponible</Typography>
      </div>
    )
  }

  return (
    <DetailedTable<TripFolderMission>
      groupBy={'appointmentTime'}
      data={missions || []}
      headerClassName="px-8"
      columns={columns}
    />
  )
}

export default TripFolderMissionSection
